import { nanoid } from 'nanoid';
import { isImage } from '@/utilities/fileExtensions';
import { ResourceType } from '@/types/resources';

export default {
  computed: {
    isPageUpload() {
      return (
        (this.$store.getters.getPageUploadPopup?.id &&
          this.$store.getters.getPageUploadPopup.id !== '') ||
        this.$store.getters.getPageIconPickerDisplayed
      );
    },
    isComment() {
      return Object.keys(this.$store.getters.getCardCommentAttachmentUploadPopup).length > 0;
    },

    isBoard() {
      return this.$route.name === 'board';
    },

    isCardDescription() {
      return Object.keys(this.$store.getters.getCardDescriptionAttachmentUploadPopup).length > 0;
    },

    isAnyCardDisplayed() {
      return this.$store.getters.getIsAnyCardDisplayed;
    },

    cardQuickViewDrop() {
      return this.$route.name === 'boards' || this.isAnyCardDisplayed;
    },

    currentInboxResource() {
      return this.$store.getters.getCurrentInboxResource;
    },

    cardInbox() {
      return this.currentInboxResource?.resource_type === ResourceType.Card;
    },

    pageInbox() {
      return this.currentInboxResource?.resource_type === ResourceType.Page;
    },

    inboxSprint() {
      return (
        this.$route.name === this.$constants.routeNames.inbox &&
        this.currentInboxResource.resource_type === ResourceType.Sprint
      );
    },

    isSprint() {
      return this.$route.name === this.$constants.routeNames.sprint || this.inboxSprint;
    },
  },
  methods: {
    getExtension(filename = '') {
      const parts = filename.split('.');
      return parts[parts.length - 1];
    },

    isSupportedFile(file) {
      if (!file) return false;
      if (!this.supportedFiles?.length) return true;
      const extension = this.getExtension(file.name);
      return this.supportedFiles?.includes?.(extension.toUpperCase());
    },

    setArrays(fileToUpload, fileToDisplay) {
      const showIcon = !isImage(fileToUpload.type);
      const style = this.generateFileImageStyle(fileToUpload.type, fileToDisplay.target.result);
      this.filesToUpload.push(fileToUpload);
      this.filesToDisplay.push({
        name: fileToUpload.name,
        size: fileToUpload.size,
        url: fileToDisplay.target.result,
        local_id: nanoid(6),
        showIcon,
        style,
        type: fileToUpload.type,
        time_created: Date.now(),
        uploadProgress: 0,
        fileSize: fileToUpload.size,
      });
    },

    readFile(file) {
      return new Promise((resolve, reject) => {
        if (!this.isSupportedFile?.(file)) {
          this.$store.dispatch('addUiNotification', {
            message: `${file.name} - ${this.translate(
              'hasUnsupportedFileType'
            )} "${this.getExtension(file.name)}"`,
            status: this.$constants.uiNotificationStatuses.error,
            duration: 6000,
          });
          throw reject();
        }
        const reader = new FileReader();
        reader.onload = (f) => {
          this.setArrays(file, f);
          resolve(reader.result);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    },

    /**
     * Used for card attachments only.
     */
    increaseDecreaseTotalFilesCount(id, decrease = false) {
      const card = this.$store.getters.getCardById(id);
      const totalFilesCount = card.total_files;

      const newFilesCount = decrease ? totalFilesCount - 1 : totalFilesCount + 1;
      this.$store.dispatch('localUpdateCardField', {
        cardId: id,
        fieldName: 'total_files',
        fieldValue: newFilesCount,
      });
    },

    insertFile(id) {
      /**
       * Checking if upload happens in a text editor
       */
      if (
        (this.isNewPage && !this.isAnyCardDisplayed) ||
        (this.isPage && !this.isAnyCardDisplayed) ||
        this.isComment ||
        (this.pageInbox && !this.isAnyCardDisplayed) ||
        this.isCardDescription ||
        this.isPageUpload
      ) {
        /**
         * Used for emitting insertImage
         */
        this.emitFileData();
        this.closePopup();
        return;
      }

      /**
       * This 'while' never happens in an editor
       * due to the previous early return.
       */
      const uploadPromises = [];
      while (this.filesToUpload.length) {
        const file = this.filesToUpload.shift();
        const fileToDisplay = this.filesToDisplay.shift();
        const formData = new FormData();
        formData.append('files', file, file.name);
        this.$store.dispatch('localAddAttachment', { attachment: fileToDisplay, id, prefix: 'c' });
        let entityTypeName = 'page_id';

        // save file and local id
        const localFileId = fileToDisplay.local_id;

        if (
          this.isBoard ||
          this.cardQuickViewDrop ||
          this.cardInbox ||
          this.isAnyCardDisplayed ||
          this.isSprint
        ) {
          entityTypeName = 'card_id';
          formData.append(entityTypeName, id);
        }

        this.increaseDecreaseTotalFilesCount(id);

        const promise = this.$store
          .dispatch('uploadFiles', {
            payload: formData,
            id,
            localId: localFileId,
            showActualFileSize: true,
          })
          .then(({ data }) => {
            if (this.isBoard || this.cardQuickViewDrop || this.cardInbox || this.isSprint) {
              this.$store.dispatch('updateCardAttachmentId', {
                attachment: data.files,
                localId: localFileId,
                cardId: id,
              });
            }
          })
          .catch((/* er */) => {
            if (this.isAnyCardDisplayed) {
              this.increaseDecreaseTotalFilesCount(id, true);
              this.$store.dispatch('setUploadFailedFlag', {
                localId: localFileId,
                file,
                cardId: id,
              });
            }
          });

        // store each promise in array to be able to detect when all of them finished
        uploadPromises.push(promise);
      }

      // when each promise is finished clear data
      Promise.all(uploadPromises).then(() => {
        this.filesToUpload = [];
        this.filesToDisplay = [];
        this.$store.dispatch('setCardAttachmentUploadingId', -1);
      });
    },

    generateFileImageStyle(type, result) {
      const upperType = (type || '').toUpperCase();
      if (upperType.includes('SVG+XML')) {
        return {
          'background-size': '100%',
          'background-repeat': 'no-repeat',
          'background-image': `url(${result})`,
        };
      }
      if (upperType.includes('GIF')) {
        return {
          'background-size': '100% 100%',
          'background-image': `url(${result})`,
        };
      }
      if (isImage(type)) {
        return {
          'background-image': `url(${result})`,
        };
      }
      return {
        background: 'linear-gradient(123.31deg, #DADCDF -0.03%, rgba(255, 255, 255, 0) 115.1%)',
        border: '1px solid #DADCDF',
      };
    },

    setSupportedFiles() {
      const formatedSupportedFiles = [];
      this.supportedFiles?.forEach((file, index) => {
        formatedSupportedFiles[index] = `.${file.toLowerCase()}`;
      });
      this.fileInput.setAttribute('accept', formatedSupportedFiles);
    },

    fileDialog() {
      this.setSupportedFiles();
      this.fileInput.dispatchEvent(new MouseEvent('click'));
      return false;
    },

    isSizeAllowed(size = 0) {
      return size <= this.allowedFileSize;
    },
  },
};
